'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { CategorySliderProps } from '@eliadmin/core/src/components/header/category-slider';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { HoverCard } from '@eliadmin/core/src/components/ui/hover-card';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { HoverCardProps } from '@radix-ui/react-hover-card';
import { HoverCardContent, HoverCardTrigger } from '@radix-ui/react-hover-card';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';
import React, { useEffect, useRef, useState, type ReactNode } from 'react';
// import CategoryCardProducts from '@/app/components/CategoryCardProducts';

export type CategoryButtonProps = {
  icon?: ReactNode;
  text?: string;
  categories?: CategoriesEntity[];
  className?: {
    root?: string;
    card?: string;
    categorySlider?: CategorySliderProps['className'];
  };

  hoverCardProps?: HoverCardProps;
};

export function CategoryButton({
  icon = HEADER_CONFIG.categoryButton.icon,
  text = HEADER_CONFIG.categoryButton.text,
  hoverCardProps,
  categories,
  ...props
}: CategoryButtonProps) {
  const { t } = useTranslations();
  const [hoveredCategory, setHoveredCategory] = useState<number | null>(null);
  const subcategoryRef = useRef<HTMLDivElement>(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  const pathname = usePathname();

  useEffect(() => {
    setOpen(false);
    handleCategoryHover(null);
  }, [pathname]);

  const handleCategoryHover = (index: number | null) => {
    setHoveredCategory(index);
  };

  const handleResize = () => {
    setWindowWidth(typeof window !== 'undefined' ? window.innerWidth : 0);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  useEffect(() => {
    if (subcategoryRef.current && typeof window !== 'undefined') {
      subcategoryRef.current.style.position = 'absolute';
      subcategoryRef.current.style.top = '0px';
      subcategoryRef.current.style.left = '1px';
      subcategoryRef.current.style.transform = 'translate(204px, 0px)';
      if (windowWidth < 1280) {
        subcategoryRef.current.style.width = windowWidth / 1.26 + 'px';
      } else {
        subcategoryRef.current.style.width = '1040px';
      }
    }
  }, [windowWidth, hoveredCategory]);

  const [open, setOpen] = useState(false);

  const timerRef = useRef<number | undefined>(undefined);

  const handleMouseOver = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setOpen(true);
  };

  const handleMouseLeave = () => {
    timerRef.current = window.setTimeout(() => {
      setOpen(false);
    }, 100) as unknown as number; // Type casting to number
    setHoveredCategory(null);
  };

  return (
    <HoverCard openDelay={0} open={open} closeDelay={0} {...hoverCardProps}>
      <HoverCardTrigger asChild onMouseOver={handleMouseOver} onMouseLeave={handleMouseLeave}>
        <Button
          variant="default"
          className={cn(
            'cursor-pointer gap-2 rounded-md bg-primary hover:bg-black hover:text-white',
            'ea-category-button',
            props.className?.root,
          )}
        >
          {icon} {t(text)}
        </Button>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
        className={cn(
          'relative z-10 h-full min-h-[500px] w-full bg-white p-2',
          'ea-category-button__card',
          props.className?.card,
        )}
        style={{
          boxShadow:
            hoveredCategory !== null
              ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), -4px 0 6px -1px rgba(0, 0, 0, 0.1)'
              : '0 4px 6px -1px rgba(0, 0, 0, 0.1), -4px 0 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          borderRadius: hoveredCategory !== null ? '12px 0 0 12px' : '12px',
        }}
      >
        <div className="flex h-full min-h-[600px] w-full max-w-[240px] flex-col gap-y-2 rounded-lg bg-input p-2">
          {categories?.map((category, index) => {
            // console.log(category.childs?.map(item => item.childs));
            return (
              <React.Fragment
                key={`category-${category.id ?? category.slug ?? category.name ?? index}`}
              >
                <li
                  onMouseEnter={() => handleCategoryHover(index)}
                  className={cn(
                    `flex w-full min-w-[200px] ${
                      hoveredCategory === index ? 'bg-primary text-white' : 'bg-transparent'
                    } flex-row items-center justify-between rounded-md`,
                    'ea-category-slider__list-item',
                  )}
                >
                  <Link
                    href={category.slug}
                    prefetchOnHover
                    className={cn(
                      'flex max-h-min w-full flex-row items-center justify-start gap-2 rounded-md px-3 py-2 text-[16px] font-bold',
                      'ea-category-slider__list-item-link',
                    )}
                  >
                    {category.image && (
                      <Image
                        alt={resolveImageAlt(category?.image, category?.name, undefined)}
                        src={category.image.url}
                        width={0}
                        height={0}
                        sizes={'100vw'}
                        className={cn('h-5 w-5', 'ea-category-slider__list-item-image')}
                      />
                    )}
                    <span className="text-left text-[16px] font-medium">{category.name}</span>
                  </Link>
                </li>
                {/* subcategory div */}
                {category.childs?.length === 0
                  ? null
                  : hoveredCategory === index &&
                    category.childs && (
                      <div
                        ref={subcategoryRef}
                        key={`${index}-${category.name}-${category.slug}`}
                        className={`absolute left-[-50px] -z-10 grid h-full min-h-[300px] w-full grid-cols-2 overflow-y-scroll overscroll-contain rounded-e-lg bg-white px-4 py-2 md:grid-cols-3 lg:grid-cols-4`}
                        style={{
                          boxShadow:
                            hoveredCategory !== null
                              ? '4px 0 6px -1px rgba(0, 0, 0, 0.1), 2px 0 4px -1px rgba(0, 0, 0, 0.06), 0 0 0 0 rgba(0, 0, 0, 0), 0 0 0 0 rgba(0, 0, 0, 0)'
                              : '',
                          gridAutoRows: 'min-content',
                        }}
                      >
                        {/* subcategory list */}
                        {category.childs.map(childCategory => {
                          const subcategoriesToDisplay = childCategory.childs?.slice(0, 4);

                          return (
                            <div
                              className="h- flex flex-col items-center justify-start gap-2"
                              key={`${childCategory.name}-${childCategory.slug}`}
                            >
                              <Link prefetchOnHover href={childCategory.slug} prefetch={false}>
                                {childCategory.icon ? (
                                  <Image
                                    src={childCategory.icon.url}
                                    alt={resolveImageAlt(childCategory?.icon, childCategory?.name)}
                                    width={48}
                                    height={48}
                                    sizes="100vw"
                                  />
                                ) : childCategory.image ? (
                                  <Image
                                    src={childCategory.image.url}
                                    alt={resolveImageAlt(childCategory?.image, childCategory?.name)}
                                    width={64}
                                    height={64}
                                    sizes="100vw"
                                  />
                                ) : (
                                  <Image
                                    src={'/medistore-favicon.jpg'}
                                    alt={childCategory.name}
                                    width={48}
                                    height={48}
                                    sizes="100vw"
                                  />
                                )}
                              </Link>
                              <Link
                                prefetchOnHover
                                href={childCategory.slug}
                                className="flex flex-col items-center justify-start gap-2 text-sm font-bold text-[#222222] hover:text-primary"
                              >
                                {childCategory.name}
                              </Link>
                              {/* childcategory subcategory */}
                              <ul className="">
                                {subcategoriesToDisplay?.map((subcategory, subcategoryIndex) => (
                                  <li
                                    className="text-sm"
                                    key={`subcategory-${subcategory.id ?? subcategory.slug ?? subcategory.name ?? subcategoryIndex}`}
                                  >
                                    <Link
                                      prefetchOnHover
                                      href={subcategory.slug}
                                      className="hover:text-primary"
                                    >
                                      {subcategory.name}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                              {childCategory.childs && childCategory.childs.length > 4 && (
                                <Link
                                  prefetchOnHover
                                  href={childCategory.slug}
                                  className="text-primary"
                                >
                                  {t('text3161')}
                                </Link>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
              </React.Fragment>
            );
          })}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
